/**
 * Analytics metric glossary — single source for tooltips and the
 * "Explain this metric" drawer.
 *
 * Copy is derived from the analyseit domain definitions so it matches how the
 * numbers are actually computed. Each entry:
 *   { key, label, short, definition, formula?, howToRead?, gotchas?[], related?[] }
 * `short` powers the inline tooltip; the rest renders in MetricDrawer.
 *
 * Mirrors klikdocs/docs/proposals/analytics-ui-overhaul/metric-glossary.md.
 */

export const METRIC_GLOSSARY = {
  grossRevenue: {
    key: 'grossRevenue',
    label: 'Gross Revenue',
    short: 'Total order value before any deductions.',
    definition:
      'The full transaction value of orders — item prices plus delivery fee plus any additional fees — before discounts, service fees, or cancellations are taken out.',
    formula: 'item subtotal + delivery fee + additional fees',
    howToRead:
      'The top-of-funnel "demand" number. Rising gross with flat net means discounts and fees are eating your margin.',
    related: ['realizedRevenue', 'netRevenue', 'lostRevenue'],
  },
  realizedRevenue: {
    key: 'realizedRevenue',
    label: 'Realized Revenue',
    short: 'Gross revenue minus what was lost to cancellations.',
    definition:
      'Revenue from orders that actually progressed (completed + voided) — gross revenue with lost/cancelled value removed.',
    formula: 'Gross Revenue − Lost Revenue',
    howToRead: 'What you actually transacted, before discounts and fees.',
    related: ['grossRevenue', 'netRevenue', 'lostRevenue'],
  },
  netRevenue: {
    key: 'netRevenue',
    label: 'Net Revenue',
    short: 'What you keep after discounts and service fees.',
    definition:
      'Realized revenue after subtracting all discounts and the platform service fee. The closest figure to "money in your pocket" in this dashboard.',
    formula: 'Realized Revenue − Discount − Service Fee',
    howToRead:
      'The bottom-line trend to watch. Compare against gross to see total leakage.',
    gotchas: [
      'Service fee can be negative for some order types, which nudges net up.',
    ],
    related: ['realizedRevenue', 'discount', 'serviceFee'],
  },
  lostRevenue: {
    key: 'lostRevenue',
    label: 'Lost Revenue',
    short: 'Value of orders cancelled before completion.',
    definition:
      'The opportunity cost of orders cancelled before they completed — demand you captured but did not fulfil.',
    howToRead:
      'Pair with Cancellation Rate and Busy Mode minutes — spikes often correlate with kitchens going busy.',
    related: ['cancellationRate', 'busyMode', 'voidedRevenue'],
  },
  voidedRevenue: {
    key: 'voidedRevenue',
    label: 'Voided Revenue',
    short: 'Value of completed orders later voided/refunded.',
    definition:
      'Revenue from orders that completed but were subsequently voided (status 5) — refunded after the fact. Distinct from lost revenue, which never completed.',
    related: ['lostRevenue', 'netRevenue'],
  },
  serviceFee: {
    key: 'serviceFee',
    label: 'Service Fee',
    short: 'Platform commission applied to the order.',
    definition:
      'The platform/commission fee component, subtracted when computing net revenue.',
    gotchas: ['Can be negative for certain order types.'],
  },
  totalOrders: {
    key: 'totalOrders',
    label: 'Total Orders',
    short: 'All orders in the selected range.',
    definition:
      'Count of every order across the selected branches/brands/aggregators/channels, regardless of final status.',
  },
  completedOrders: {
    key: 'completedOrders',
    label: 'Completed Orders',
    short: 'Orders fulfilled successfully.',
    definition: 'Orders that reached completed status (status 4).',
  },
  cancelledOrders: {
    key: 'cancelledOrders',
    label: 'Cancelled Orders',
    short: 'Orders cancelled before completion.',
    definition:
      'Orders cancelled before completing (status 3). Drives Lost Revenue.',
    related: ['lostRevenue', 'cancellationRate'],
  },
  cancellationRate: {
    key: 'cancellationRate',
    label: 'Cancellation Rate',
    short: 'Share of orders that were cancelled.',
    definition: 'The proportion of total orders that were cancelled.',
    formula: 'Cancelled Orders ÷ Total Orders',
    howToRead:
      'A health/operations signal. Read alongside cancellation reasons and busy-mode minutes.',
    related: ['cancelledOrders', 'lostRevenue', 'busyMode'],
  },
  trendBadge: {
    key: 'trendBadge',
    label: 'Trend vs previous period',
    short: 'Change versus the comparable previous period.',
    definition:
      'Powered by the analyseit v2 comparison metrics. Each headline metric returns a percentage change and a status of GAIN, LOSE, or NEUTRAL.',
    gotchas: [
      'A "gain" is directional, not always good — a gain in cancelled orders is bad. Colour follows the metric\'s desirability, not the raw sign.',
    ],
  },
  avgBasketSize: {
    key: 'avgBasketSize',
    label: 'Avg Basket Size',
    short: 'Average value of an order.',
    definition:
      'Average order value. Completed basket = Net Revenue ÷ Completed Orders; cancelled basket = Lost Revenue ÷ Cancelled Orders.',
    formula: 'Net Revenue ÷ Completed Orders',
    howToRead:
      'Upsell and bundling move this. Watch the completed-vs-cancelled split.',
  },
  discount: {
    key: 'discount',
    label: 'Discount',
    short: 'Total discount, split by who funded it.',
    definition:
      'Total discount broken down by source: merchant-funded, provider-subsidised (aggregator), custom adjustments, promo/reward/campaign/voucher (each split merchant vs provider), waived delivery fees, and statutory discounts (Senior Citizen, PWD, Solo Parent).',
    howToRead:
      'High merchant-funded discount erodes net revenue directly; provider-funded does not.',
    related: ['netRevenue'],
  },
  busyMode: {
    key: 'busyMode',
    label: 'Busy Mode',
    short: 'Total time a branch was toggled "busy".',
    definition:
      'Duration (minutes) a branch had busy mode on, with start/end and a reason. Used to explain order delays and cancellations.',
    howToRead:
      'Overlay against Lost Revenue and Cancellation Rate — busy spikes often precede cancellations.',
    related: ['lostRevenue', 'cancellationRate'],
  },
  menuReportWindow: {
    key: 'menuReportWindow',
    label: 'Menu report availability',
    short: 'Menu-level reports cover the last 7 days.',
    definition:
      'Item, Category, and Modifier breakdowns (and their CSV exports) are available for a rolling 7-day window. This is a backend aggregation limit — unlike order/revenue/KPI reports, per-item data is not kept available for longer ranges.',
    howToRead:
      'If your dashboard range is wider than 7 days, the menu breakdown shows just the most recent 7 days (you will see a "Showing last 7 days" note). Narrow the range to control which 7 days.',
    gotchas: [
      "Don't compare a 7-day menu breakdown against a 30-day revenue total — the windows differ.",
      'The backend does not warn about this, which is why the UI caps it for you.',
    ],
  },
};

/** Look up an entry by key. Returns undefined if not found. */
export const getMetric = key => METRIC_GLOSSARY[key];
